import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Pagination, Paper, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import SearchableSelect from '../common/SearchableSelect';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { useToast } from '../../context/ToastContext';
import { EmployeeLoanService } from '../../services/employeeLoan.service';
import {
    IEmployeeLoan, ILoanCreateRequest, ILoanInstallment, IOutstandingLoanSummary, LoanStatus,
} from '../../interfaces/employeeLoan.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import EmployeeLoanFormDialog from './EmployeeLoanFormDialog';
import EmployeeLoanDetailDialog from './EmployeeLoanDetailDialog';
import LoanOpeningBalanceDialog from './LoanOpeningBalanceDialog';
import { useLoanAccounting } from '../../hooks/useLoanAccounting';
import { LoanStatusChip, loanPeriod, loanRef, loanTypeLabel } from './loanLabels';

const PAGE_SIZE = 20;
const STATUSES: LoanStatus[] = ['REQUESTED', 'APPROVED', 'ACTIVE', 'CLOSED', 'REJECTED', 'CANCELLED', 'WRITTEN_OFF'];

// W3-D: manager view of employee loans and salary advances: requests to approve, approved loans to
// disburse, active loans to recover, plus the funding view and the legacy-recovery reclass register.
const EmployeeLoans: React.FC = () => {
    const { showSuccess, showApiError } = useToast();
    const canManage = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    const [rows, setRows] = useState<IEmployeeLoan[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<LoanStatus | ''>('');
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [summary, setSummary] = useState<IOutstandingLoanSummary | null>(null);
    const [reclass, setReclass] = useState<ILoanInstallment[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [reclassing, setReclassing] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const { preview: opening, reload: reloadOpening } = useLoanAccounting(institutionId);
    const [openingOpen, setOpeningOpen] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            const [pageData, sum, rc] = await Promise.all([
                EmployeeLoanService.list(institutionId, {
                    status: status || undefined, userId: employee?.id, page, size: PAGE_SIZE,
                }),
                EmployeeLoanService.outstanding(institutionId),
                EmployeeLoanService.reclassRegister(institutionId),
            ]);
            setRows(pageData.content);
            setTotalPages(pageData.totalPages);
            setSummary(sum);
            setReclass(rc);
        } catch (e) {
            showApiError(e, 'Failed to load employee loans');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, status, employee, page]);

    useEffect(() => { load(); }, [load]);

    const create = async (req: ILoanCreateRequest) => {
        setSaving(true);
        try {
            const created = await EmployeeLoanService.create(req);
            setFormOpen(false);
            showSuccess('Loan created. Disburse it to pay the employee and start recovery.');
            await load();
            setSelectedId(created.id);
        } catch (e) {
            showApiError(e, 'Failed to create the loan');
        } finally {
            setSaving(false);
        }
    };

    const postReclass = async () => {
        setReclassing(true);
        try {
            await EmployeeLoanService.reclass(institutionId, reclass.map(r => r.id));
            showSuccess('Reclass journals posted');
            await load();
        } catch (e) {
            showApiError(e, 'Failed to post the reclass journals');
        } finally {
            setReclassing(false);
        }
    };

    const columns: Column<IEmployeeLoan>[] = [
        { key: 'ref', header: 'Loan no.', priority: 1, helpKey: 'payroll.loans.col.ref', render: r => loanRef(r.id) },
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.loans.col.emp', render: r => r.employeeName || '—' },
        { key: 'type', header: 'Type', priority: 2, helpKey: 'payroll.loans.col.type', render: r => loanTypeLabel(r.loanType) },
        { key: 'principal', header: 'Amount', priority: 1, align: 'right', helpKey: 'payroll.loans.col.principal', render: r => formatMoney(r.principal) },
        { key: 'out', header: 'Outstanding', priority: 1, align: 'right', helpKey: 'payroll.loans.col.out', render: r => formatMoney(r.outstanding) },
        {
            key: 'sched', header: 'Schedule', priority: 3,
            helpKey: 'payroll.loans.col.sched', render: r => `${r.installmentCount} x ${formatMoney(r.installmentAmount)} from ${loanPeriod(r.startYear, r.startMonth)}`,
        },
        { key: 'status', header: 'Status', priority: 1, helpKey: 'payroll.loans.col.status', render: r => <LoanStatusChip status={r.status} /> },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.loans.col.actions', role: 'actions', hideInCard: true,
            render: r => <Button size="small" data-help="payroll.loans.action.open" onClick={() => setSelectedId(r.id)}>Open</Button>,
        },
    ];

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="payroll.loans.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }} data-help-section="payroll.loans.toolbar">
                <Typography variant="h5">Employee Loans and Advances</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {isSystemAdmin && (
                        <SearchableSelect
                            label="Select Institution"
                            data-help="payroll.loans.institution"
                            options={institutions}
                            value={institutionId}
                            onChange={id => { setInstitutionId(id); setPage(0); }}
                            getOptionLabel={i => i.name}
                            clearable={false}
                            sx={{ minWidth: 220 }}
                        />
                    )}
                    {canManage && (
                        
                        <Button data-help="payroll.loans.new" variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)} disabled={!institutionId}>
                            New loan or advance
                        </Button>
                        
                    )}
                </Box>
            </Box>

            {summary && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 220px', maxWidth: 320 }} data-help="payroll.loans.summaryOutstanding">
                        <Typography variant="caption" color="text.secondary">Outstanding to recover</Typography>
                        <Typography variant="h6">{formatMoney(summary.totalOutstanding)}</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 220px', maxWidth: 320 }} data-help="payroll.loans.summaryApproved">
                        <Typography variant="caption" color="text.secondary">Approved, not yet disbursed</Typography>
                        <Typography variant="h6">{formatMoney(summary.totalApprovedNotDisbursed)}</Typography>
                    </Paper>
                </Box>
            )}

            {canManage && opening && opening.accountingOn && opening.loanCount > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    data-testid="loan-opening-balance-banner"
                    action={<Button data-help="payroll.loans.openingBalance.open" color="inherit" size="small" onClick={() => setOpeningOpen(true)}>Bring into books</Button>}
                >
                    {opening.loanCount} loan(s) with {formatMoney(opening.total)} still to recover were paid out before the
                    Accounting module was on and are not in the books yet. Post one opening-balance entry to add them.
                </Alert>
            )}

            {canManage && reclass.length > 0 && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={<Button data-help="payroll.loans.reclass" color="inherit" size="small" onClick={postReclass} disabled={reclassing}>Post reclass</Button>}
                >
                    {reclass.length} loan deduction(s) were taken from salary under the legacy payment mode and are still booked in
                    "Other deductions payable". Post the reclass to move them to the employee loan receivable.
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="payroll.loans.filters">
                <SearchableSelect
                    data-help="payroll.loans.filterStatus"
                    label="Status"
                    size="small"
                    options={STATUSES.map(s => ({ id: s, label: humanizeEnum(s) }))}
                    value={status}
                    onChange={id => { setStatus(id as LoanStatus | ''); setPage(0); }}
                    getOptionLabel={o => o.label}
                    sx={{ flex: '1 1 200px', maxWidth: 280 }}
                />
                <EmployeeAutocomplete
                    data-help="payroll.loans.filterEmployee"
                    value={employee}
                    onChange={e => { setEmployee(e); setPage(0); }}
                    sx={{ flex: '1 1 260px', maxWidth: 360 }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <>
                    <ResponsiveTable data-help-section="payroll.loans.table"
                        pageKey="employee-loans"
                        columns={columns}
                        rows={rows}
                        getRowKey={r => r.id}
                        emptyMessage="No loans or salary advances yet."
                    />
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination data-help="payroll.loans.pager" count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                        </Box>
                    )}
                </>
            )}

            <EmployeeLoanFormDialog open={formOpen} institutionId={institutionId} saving={saving}
                onClose={() => setFormOpen(false)} onSave={create} />
            <EmployeeLoanDetailDialog loanId={selectedId} onClose={() => setSelectedId(null)} onChanged={() => { load(); reloadOpening(); }} />
            {opening && (
                <LoanOpeningBalanceDialog open={openingOpen} institutionId={institutionId} preview={opening}
                    onClose={() => setOpeningOpen(false)}
                    onPosted={() => { setOpeningOpen(false); reloadOpening(); load(); }} />
            )}
        </Box>
    );
};

export default EmployeeLoans;
